import { KeyboardEvent, ReactElement, useState } from 'react'
import { Add } from '@mui/icons-material'
import { Box, Chip, Popover, TextField } from '@mui/material'

export interface TagsFieldProps {
  value: string[]
  onChange: (tags: string[]) => void
  disabled?: boolean
}

/**
 * Editable tag chips: click a chip to remove it, "Neu" opens an input for comma-separated tags
 * (Enter adds, Escape cancels).
 */
const TagsField = ({ value: tags, onChange, disabled = false }: TagsFieldProps): ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [newTag, setNewTag] = useState('')

  const closePopover = () => {
    setAnchorEl(null)
    setNewTag('')
  }

  const removeTag = (tag: string) => onChange(tags.filter(t => t !== tag))

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && newTag.trim()) {
      event.preventDefault()
      const added = newTag
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !tags.includes(tag))
      if (added.length) onChange([...tags, ...added])
      closePopover()
    } else if (event.key === 'Escape') {
      closePopover()
    }
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {tags.map(tag => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          disabled={disabled}
          onDelete={() => removeTag(tag)}
          onClick={() => removeTag(tag)}
        />
      ))}
      <Chip
        icon={<Add />}
        label="Neu"
        size="small"
        color="primary"
        disabled={disabled}
        onClick={event => setAnchorEl(event.currentTarget)}
      />
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <TextField
          autoFocus
          size="small"
          value={newTag}
          onChange={event => setNewTag(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={closePopover}
          placeholder="Komma separierte Tags (Enter zum Hinzufügen)"
          multiline
          rows={2}
          sx={{ m: 1, minWidth: 250 }}
        />
      </Popover>
    </Box>
  )
}

export default TagsField
